import qs from 'qs';
import { z } from 'zod4';

import { SortDirection } from '../../../components/table/sortable-header';
import { positiveInteger } from '../../../lib/url/hooks/use-queries-control';
import { enabled } from '../../../lib/who-am-i';
import { FitlersAPIData } from '../../../queries/rewards';

export const createHistoryDetailParamParser = () => {
	return [
		['page', 'sortBy', 'sortDirection'],
		{
			defaultValue: {
				page: '1',
				sortBy: 'created_at',
				sortDirection: SortDirection.DESC,
			},
			allowedValues: {
				sortBy: ['created_at'],
				sortDirection: [SortDirection.ASC, SortDirection.DESC],
			},
			transforms: {
				page: positiveInteger,
			},
		},
	] as const;
};

export const defaultCollectionKey = (collections: FitlersAPIData['collections']) => {
	if (!collections || collections.length === 0) return 'all';
	return collections[0].key;
};

/**
 * Creates a Zod schema for validating rewards index params with dynamic collection and subcategory validation.
 * Ensures that filters.collection is always a valid string from the allowed collections.
 */
export const createRewardsIndexParamsSchema = (
	{
		allowedCollectionKeys,
		allowedSubcategories,
	}: {
		allowedCollectionKeys: string[];
		allowedSubcategories: string[];
	} = { allowedCollectionKeys: [], allowedSubcategories: [] }
) => {
	const defaultKey = enabled('rewards-mini-page') ? 'all' : allowedCollectionKeys[0] || 'all';

	return z
		.object({
			q: z.string().optional().nullable().catch(undefined),
			filters: z
				.object({
					// Collection must be a string and one of the allowed keys
					// If invalid or missing, defaults to the first available collection
					collection: z
						.union([
							z.string(),
							// If somehow an array is passed, take the first element
							z.array(z.string()).transform((arr) => arr[0] || defaultKey),
						])
						.refine((key) => allowedCollectionKeys.includes(key), {
							message: 'Invalid collection key',
						})
						.catch(defaultKey)
						.default(defaultKey),
					// Coerce single values to arrays for consistency
					price: z
						.union([z.string().transform((s) => [s]), z.array(z.string())])
						.optional()
						.catch(undefined),
					type: z
						.union([z.string().transform((s) => [s]), z.array(z.string())])
						.optional()
						.catch(undefined),
					category: z
						.union([z.string().transform((s) => [s]), z.array(z.string())])
						.optional()
						.catch(undefined),
					// Subcategory filter - validated against allowed values if provided
					subCategory: z
						.string()
						.refine((key) => allowedSubcategories.length === 0 || allowedSubcategories.includes(key), {
							message: 'Invalid subcategory',
						})
						.optional()
						.catch(undefined),
				})
				.default({ collection: defaultKey }),
			pageNumber: z
				.union([z.string().transform((val) => positiveInteger(val)), z.number().transform((val) => Math.max(1, Math.floor(val)))])
				.catch(1)
				.default(1),
		})
		.catch({ filters: { collection: defaultKey }, pageNumber: 1 });
};

export type ParsedRewardsIndexParams = z.infer<ReturnType<typeof createRewardsIndexParamsSchema>>;

/**
 * Parses and validates index params from a URL search string.
 * Guarantees that filters.collection will always be a valid string.
 */
export const parseIndexParamsFromSearch = ({
	search,
	collections,
	subCategories,
}: {
	search: string;
	collections: FitlersAPIData['collections'];
	subCategories: string[];
}) => {
	const allowedKeys = collections.map((c) => c.key);
	const schema = createRewardsIndexParamsSchema({ allowedCollectionKeys: allowedKeys, allowedSubcategories: subCategories });

	const paramsObject = qs.parse(search, { ignoreQueryPrefix: true });
	const indexParams = schema.parse(paramsObject);

	return indexParams;
};
